import { BaseApiUrl } from "@/constants";
import { APIResponse } from "@/interfaces";


class ApiService {
  private baseURL: string;

  constructor() {
    // Fetch the base URL from environment variables
    this.baseURL = BaseApiUrl;
  }

  private async request<T>(
    method: "GET" | "POST" | "PUT" | "DELETE",
    endpoint: string,
    data: unknown = null,
    params: Record<string, string | number> = {}
  ): Promise<APIResponse<T>> {
    try {
      const url = this._buildURL(endpoint, params);
      const options: RequestInit = {
        credentials: "include",
        method,
        headers: {
          "Content-Type": "application/json",
          "Device-Name": navigator.userAgent,
          "Device-Id": crypto.randomUUID(),
          // Electron identifies via App-Name; website is identified by the browser's Origin
          ...(window.electron?.isElectron
            ? { "App-Name": "com.modulussell.windows" }
            : {}),
        },
      };

      if (data) {
        options.body = JSON.stringify(data);
      }
      const response = await fetch(url, options);

      this._checkResponseStatus(response);
      return (await response.json()) as APIResponse<T>;
    } catch (error) {
      console.error(`Error during ${method} request to ${endpoint}:`, error);
      throw error; // Propagate the error
    }
  }

  public async get<T>(
    endpoint: string,
    params: Record<string, string | number> = {}
  ): Promise<APIResponse<T>> {
    return this.request<T>("GET", endpoint, null, params);
  }

  public async post<T>(
    endpoint: string,
    data: unknown
  ): Promise<APIResponse<T>> {
    return this.request<T>("POST", endpoint, data);
  }

  public async put<T>(
    endpoint: string,
    data: unknown
  ): Promise<APIResponse<T>> {
    return this.request<T>("PUT", endpoint, data);
  }

  public async delete<T>(endpoint: string): Promise<APIResponse<T>> {
    return this.request<T>("DELETE", endpoint);
  }

  private _buildURL(
    endpoint: string,
    params: Record<string, string | number>
  ): string {
    const url = new URL(`${this.baseURL}${endpoint}`);
    Object.keys(params).forEach((key) =>
      url.searchParams.append(key, String(params[key]))
    );
    return url.toString();
  }

  private _checkResponseStatus(response: Response): void {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }
}
const apiService = new ApiService();
export default apiService;